import XLSX from 'xlsx-js-style';

export interface ExportableFutureRegistration {
  id: string;
  created_at: string;
  registration_year: string;
  consultant_name: string;
  student_name: string;
  department: string;
  contract_sent: boolean;
  tracking_system_added: boolean;
  tracking_pages_added: boolean;
  groups_added: boolean;
  student_file_opened: boolean;
  resources_sent: boolean;
  sent_to_application_office: boolean;
}

export type CompletionFilter = 'all' | 'complete' | 'incomplete';

export interface RegistrationExportFilters {
  startDate: string;
  endDate: string;
  consultant: string;
  department: string;
  completion: CompletionFilter;
}

const checklistKeys: Array<keyof ExportableFutureRegistration> = [
  'contract_sent',
  'tracking_system_added',
  'tracking_pages_added',
  'groups_added',
  'student_file_opened',
  'resources_sent',
  'sent_to_application_office',
];

const toDateKey = (value: string) => {
  if (!value) return '';
  const isoDate = value.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoDate) return isoDate[1];
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const toExcelDate = (value: string) => {
  const dateKey = toDateKey(value);
  if (!dateKey) return '';
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(year, month - 1, day, 12);
};

const safeText = (value?: string | null) => {
  const text = (value || '').trim();
  return /^[=+\-@]/.test(text) ? `'${text}` : text;
};

export const isRegistrationComplete = (registration: ExportableFutureRegistration) =>
  checklistKeys.every((key) => Boolean(registration[key]));

export const filterRegistrationsForExport = (
  registrations: ExportableFutureRegistration[],
  filters: RegistrationExportFilters,
) => registrations.filter((registration) => {
  const registrationDate = toDateKey(registration.created_at);
  if (filters.startDate && registrationDate < filters.startDate) return false;
  if (filters.endDate && registrationDate > filters.endDate) return false;
  if (filters.consultant !== 'all' && registration.consultant_name !== filters.consultant) return false;
  if (filters.department !== 'all' && registration.department !== filters.department) return false;

  const complete = isRegistrationComplete(registration);
  if (filters.completion === 'complete' && !complete) return false;
  if (filters.completion === 'incomplete' && complete) return false;
  return true;
});

const filterLabel = (value: string, allLabel: string) => value === 'all' ? allLabel : value;

export const exportFutureRegistrationsToExcel = (
  registrations: ExportableFutureRegistration[],
  selectedYear: string,
  filters: RegistrationExportFilters,
) => {
  const headers = [
    'Sıra No', 'Kayıt Yılı', 'Kayıt Tarihi', 'Danışman', 'Öğrenci', 'Bölüm',
    'Sözleşme', 'Takip Sistemi', 'Takip Sayfaları', 'Gruplar', 'Öğrenci Dosyası',
    'Kaynaklar', 'Başvuru Ofisi', 'Genel Durum',
  ];
  const status = (value: boolean, positive: string, negative: string) => value ? positive : negative;
  const rows = registrations.map((registration, index) => [
    index + 1,
    safeText(registration.registration_year),
    toExcelDate(registration.created_at),
    safeText(registration.consultant_name),
    safeText(registration.student_name),
    safeText(registration.department),
    status(registration.contract_sent, 'İletildi', 'İletilmedi'),
    status(registration.tracking_system_added, 'Eklendi', 'Eklenmedi'),
    status(registration.tracking_pages_added, 'Eklendi', 'Eklenmedi'),
    status(registration.groups_added, 'Eklendi', 'Eklenmedi'),
    status(registration.student_file_opened, 'Açıldı', 'Açılmadı'),
    status(registration.resources_sent, 'İletildi', 'İletilmedi'),
    status(registration.sent_to_application_office, 'İletildi', 'İletilmedi'),
    isRegistrationComplete(registration) ? 'Tamamlandı' : 'Eksik İşlem Var',
  ]);

  const completionLabels: Record<CompletionFilter, string> = {
    all: 'Tümü', complete: 'Tamamlananlar', incomplete: 'Eksik işlemi olanlar',
  };
  const period = filters.startDate || filters.endDate
    ? `${filters.startDate || 'Başlangıç yok'} - ${filters.endDate || 'Bitiş yok'}`
    : 'Tüm yıl';
  const sheetData = [
    [`${selectedYear} Satış Kayıtları`],
    ['Satış departmanı kayıt ve takip raporu'],
    ['Dönem', period, '', 'Danışman', filterLabel(filters.consultant, 'Tümü'), '', 'Bölüm', filterLabel(filters.department, 'Tümü'), '', 'Takip Durumu', completionLabels[filters.completion]],
    ['Toplam Kayıt', registrations.length, '', 'Oluşturulma', new Date().toLocaleString('tr-TR')],
    [],
    headers,
    ...rows,
  ];

  const worksheet = XLSX.utils.aoa_to_sheet(sheetData, { cellDates: true });
  worksheet['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: headers.length - 1 } },
    { s: { r: 1, c: 0 }, e: { r: 1, c: headers.length - 1 } },
    { s: { r: 2, c: 1 }, e: { r: 2, c: 2 } },
    { s: { r: 2, c: 4 }, e: { r: 2, c: 5 } },
    { s: { r: 2, c: 7 }, e: { r: 2, c: 8 } },
    { s: { r: 2, c: 10 }, e: { r: 2, c: 13 } },
    { s: { r: 3, c: 1 }, e: { r: 3, c: 2 } },
    { s: { r: 3, c: 4 }, e: { r: 3, c: 7 } },
  ];
  worksheet['!autofilter'] = { ref: `A6:N${Math.max(6, rows.length + 6)}` };
  worksheet['!cols'] = [
    { wch: 9 }, { wch: 12 }, { wch: 14 }, { wch: 24 }, { wch: 28 }, { wch: 24 },
    { wch: 14 }, { wch: 16 }, { wch: 18 }, { wch: 14 }, { wch: 18 }, { wch: 14 },
    { wch: 18 }, { wch: 18 },
  ];
  worksheet['!rows'] = [{ hpt: 34 }, { hpt: 22 }, { hpt: 26 }, { hpt: 26 }, { hpt: 10 }, { hpt: 30 }];

  const border = {
    top: { style: 'thin', color: { rgb: 'D7DEE8' } },
    bottom: { style: 'thin', color: { rgb: 'D7DEE8' } },
    left: { style: 'thin', color: { rgb: 'D7DEE8' } },
    right: { style: 'thin', color: { rgb: 'D7DEE8' } },
  };
  const titleCell = worksheet.A1;
  if (titleCell) titleCell.s = {
    font: { bold: true, sz: 20, color: { rgb: 'FFFFFF' } },
    fill: { patternType: 'solid', fgColor: { rgb: '17324D' } },
    alignment: { horizontal: 'left', vertical: 'center' },
  };
  if (worksheet.A2) worksheet.A2.s = {
    font: { italic: true, sz: 11, color: { rgb: 'D9E7F5' } },
    fill: { patternType: 'solid', fgColor: { rgb: '17324D' } },
    alignment: { horizontal: 'left', vertical: 'center' },
  };

  ['A3', 'D3', 'G3', 'J3', 'A4', 'D4'].forEach((address) => {
    if (worksheet[address]) worksheet[address].s = {
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: '2563EB' } }, border,
      alignment: { horizontal: 'left', vertical: 'center' },
    };
  });
  ['B3', 'E3', 'H3', 'K3', 'B4', 'E4'].forEach((address) => {
    if (worksheet[address]) worksheet[address].s = {
      font: { bold: true, color: { rgb: '17324D' } },
      fill: { patternType: 'solid', fgColor: { rgb: 'EAF2FF' } }, border,
      alignment: { horizontal: 'left', vertical: 'center' },
    };
  });

  for (let column = 0; column < headers.length; column += 1) {
    const address = XLSX.utils.encode_cell({ r: 5, c: column });
    if (worksheet[address]) worksheet[address].s = {
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      fill: { patternType: 'solid', fgColor: { rgb: '0F766E' } }, border,
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
    };
  }

  for (let rowIndex = 6; rowIndex < rows.length + 6; rowIndex += 1) {
    for (let column = 0; column < headers.length; column += 1) {
      const address = XLSX.utils.encode_cell({ r: rowIndex, c: column });
      const cell = worksheet[address];
      if (!cell) continue;
      const isStatusColumn = column >= 6;
      const isPositiveStatus = ['İletildi', 'Eklendi', 'Açıldı', 'Tamamlandı'].includes(String(cell.v));
      cell.s = {
        font: { color: { rgb: isStatusColumn ? (isPositiveStatus ? '166534' : '991B1B') : '1F2937' } },
        fill: { patternType: 'solid', fgColor: { rgb: isStatusColumn ? (isPositiveStatus ? 'ECFDF5' : 'FEF2F2') : (rowIndex % 2 === 0 ? 'F8FAFC' : 'FFFFFF') } },
        border,
        alignment: { horizontal: column === 0 || column === 1 || column >= 6 ? 'center' : 'left', vertical: 'center', wrapText: true },
      };
    }
    const dateCell = worksheet[XLSX.utils.encode_cell({ r: rowIndex, c: 2 })];
    if (dateCell && dateCell.v instanceof Date) dateCell.z = 'dd.mm.yyyy';
    worksheet['!rows']![rowIndex] = { hpt: 24 };
  }

  const workbook = XLSX.utils.book_new();
  workbook.Props = {
    Title: `${selectedYear} Satış Kayıtları`,
    Subject: 'Satış departmanı kayıt listesi',
    Author: 'Pisa Panel',
    CreatedDate: new Date(),
  };
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Satış Kayıtları');
  const fileDate = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(workbook, `satis-kayitlari-${selectedYear}-${fileDate}.xlsx`, { compression: true });
};
